import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Modal, Box, IconButton, Button, Stack, Typography, TextField,
  CircularProgress, Chip, Alert, InputAdornment, Collapse
} from '@mui/material';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import ArrowRightOutlined from '@ant-design/icons/ArrowRightOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import SearchOutlined from '@ant-design/icons/SearchOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import IdcardOutlined from '@ant-design/icons/IdcardOutlined';
import UserAddOutlined from '@ant-design/icons/UserAddOutlined';
import axios from 'api/axiosInstance';
import endpoints from 'api/endpoints';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { formatDoctorName } from '../../utils/doctorName';
import { Stepper } from 'components/site/fx';

const outerSx = {
  position: 'absolute', top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '94%', sm: 540 },
  // vh counts the area behind mobile browser toolbars; dvh excludes them so the
  // box (and its sticky footer) never sits under the address bar. vh is fallback.
  maxHeight: '92vh',
  '@supports (max-height: 100dvh)': { maxHeight: '85dvh' },
  bgcolor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderRadius: 3,
  boxShadow: '0 24px 64px rgba(15, 23, 42, 0.22)',
  border: '1px solid rgba(255, 255, 255, 0.6)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  outline: 'none',
};
const headerSx = {
  flexShrink: 0,
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(12px)',
};
const bodySx = {
  flex: 1,
  overflowY: 'auto',
  px: 3, py: 2.5,
};
const footerSx = {
  flexShrink: 0,
  px: 3, pt: 1.75,
  // Clear the mobile browser's bottom bar / home indicator.
  pb: 'calc(14px + env(safe-area-inset-bottom))',
  borderTop: '1px solid',
  borderColor: 'divider',
  bgcolor: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(12px)',
  display: 'flex', gap: 1.5,
};

const TOTAL_STEPS = 3;

// Auto-capitalize each word: "john doe" → "John Doe"
const titleCase = (s) => s.replace(/\b\w/g, (c) => c.toUpperCase());

export default function BookAppointmentModal({
  open, onClose, onSuccess, accentColor = '#1890ff', id,
  fontFamily = "'DM Sans', system-ui, -apple-system, sans-serif",
  headingFontFamily,
  preselectedDoctorId = null, preselectedSpecId = null, offlineOnly = false,
}) {
  const params = useParams();
  // `id` prop (numeric company_id) comes from templates like TemplateImpact.
  // Fallback to URL :slug param, then :id param.
  const companyId = id || params.slug || params.id;

  // Steps: 1 = Identity (ID + Name), 2 = Contact (Phone + Email), 3 = Doctor/Date/Time
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [tokenNumber, setTokenNumber] = useState(null); // assigned for offline/walk-in bookings

  // ── Step 1: Identity ──
  const [patientType, setPatientType] = useState(null); // null | 'existing' | 'new'
  const [hospitalId, setHospitalId] = useState('');
  const [name, setName] = useState('');
  const [lookupResult, setLookupResult] = useState(null); // { found, first_name, ... }
  const [lookupLoading, setLookupLoading] = useState(false);
  const [nameLockedAlert, setNameLockedAlert] = useState(false);
  const lookupTimer = useRef(null);

  // ── Step 2: Contact ──
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  // ── Step 3: Doctor/Date/Time ──
  const [specializations, setSpecializations] = useState([]);
  const [loadingSpecs, setLoadingSpecs] = useState(false);
  const [selectedSpec, setSelectedSpec] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [availabilityData, setAvailabilityData] = useState({});
  const [availableDates, setAvailableDates] = useState([]);
  const [locationsByDate, setLocationsByDate] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [date, setDate] = useState('');
  const [doctorSlots, setDoctorSlots] = useState([]);
  const [time, setTime] = useState('');
  const [activeSection, setActiveSection] = useState('spec'); // 'spec' | 'doctor' | 'date' | 'branch' | 'time'
  const [consultationMode, setConsultationMode] = useState('IN_PERSON'); // 'IN_PERSON' | 'ONLINE'
  const [selectedBranch, setSelectedBranch] = useState(''); // location_id chosen for the day
  const [onlineMeetingLink, setOnlineMeetingLink] = useState('');
  const timeRef = useRef(null);
  const modalContentRef = useRef(null);

  // ── Reset ──
  useEffect(() => {
    if (!open) {
      setStep(1); setSubmitted(false); setErrors({}); setTokenNumber(null);
      setPatientType(null); setHospitalId(''); setName(''); setLookupResult(null);
      setPhone(''); setEmail('');
      setSpecializations([]); setSelectedSpec(''); setDoctors([]);
      setSelectedDoctor(''); setAvailabilityData({}); setAvailableDates([]);
      setLocationsByDate({});
      setDate(''); setDoctorSlots([]); setTime('');
      setConsultationMode('IN_PERSON'); setOnlineMeetingLink('');
    }
  }, [open]);

  // ── Apply preselection when modal opens ──
  // The public "Meet Our Doctors" section passes a preselected doctor and spec
  // so the user lands with those already filled in. We set spec first, then
  // queue the doctor set after the spec change-effect runs (otherwise it would
  // clear selectedDoctor as part of normal "spec changed → reset" behaviour).
  const preselectingRef = useRef(false);
  useEffect(() => {
    if (!open) return;
    if (preselectedSpecId) {
      preselectingRef.current = true;
      setSelectedSpec(preselectedSpecId);
    }
    if (preselectedDoctorId) {
      const t = setTimeout(() => {
        setSelectedDoctor(preselectedDoctorId);
        preselectingRef.current = false;
      }, 80);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Lookup by hospital ID ──
  useEffect(() => {
    if (!hospitalId || hospitalId.trim().length < 2) {
      setLookupResult(null);
      return;
    }
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(async () => {
      try {
        setLookupLoading(true);
        const resp = await axios.get(endpoints.appointment.patientLookup, {
          params: { company_id: companyId, hospital_id: hospitalId.trim() }
        });
        setLookupResult(resp.data);
        if (resp.data?.found) {
          setName(titleCase(`${resp.data.first_name || ''} ${resp.data.last_name || ''}`.trim()));
          // Prefill phone and email from DB record for step 2
          if (resp.data.mobile) setPhone(resp.data.mobile);
          if (resp.data.email) setEmail(resp.data.email);
        }
      } catch {
        setLookupResult(null);
      } finally {
        setLookupLoading(false);
      }
    }, 400);
    return () => { if (lookupTimer.current) clearTimeout(lookupTimer.current); };
  }, [hospitalId, companyId]);

  // ── Fetch specializations on step 3 ──
  useEffect(() => {
    if (step === 3 && specializations.length === 0) fetchSpecializations();
  }, [step]);

  // ── Fetch doctors when spec changes ──
  useEffect(() => {
    if (selectedSpec) {
      fetchDoctors(selectedSpec);
      setSelectedDoctor(''); setAvailabilityData({}); setAvailableDates([]);
      setDate(''); setDoctorSlots([]); setTime('');
    } else { setDoctors([]); }
  }, [selectedSpec]);

  // ── Fetch slots when doctor changes ──
  useEffect(() => {
    if (selectedDoctor) {
      fetchDoctorSlots(selectedDoctor);
      setDate(''); setDoctorSlots([]); setTime('');
      // Default the consultation mode to whatever the doctor supports.
      // If they offer both, prefer in-person; if only one, lock to it.
      const doc = doctors.find((x) => x.id === selectedDoctor);
      if (doc) {
        const inPerson = doc.in_person_enabled !== false;
        const online = !!doc.online_enabled;
        if (inPerson && !online) setConsultationMode('IN_PERSON');
        else if (!inPerson && online) setConsultationMode('ONLINE');
        else setConsultationMode((cur) => (cur === 'ONLINE' && !online) || (cur === 'IN_PERSON' && !inPerson) ? 'IN_PERSON' : cur);
      }
    } else { setAvailabilityData({}); setAvailableDates([]); }
  }, [selectedDoctor, doctors]);

  // ── Update time slots when date changes ──
  // Preserve a previously-picked time if it's still valid for the new date —
  // otherwise the user loses their selection just because they navigated
  // back/forward through steps.
  useEffect(() => {
    if (date && availabilityData[date]) {
      const newSlots = availabilityData[date].map((s) => {
        const tv = s.start.slice(0, 5);
        const [h, m] = tv.split(':').map(Number);
        return {
          value: tv,
          label: `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`,
          location_id: s.location_id ?? null,
          location_name: s.location_name ?? null,
        };
      });
      setDoctorSlots(newSlots);
      setTime((cur) => (cur && newSlots.some((s) => s.value === cur)) ? cur : '');
    } else {
      setDoctorSlots([]);
      setTime((cur) => (cur ? '' : cur));
    }
  }, [date, availabilityData]);

  // Branches available on the chosen day (derived from that day's slots).
  const branchesForDate = (() => {
    const m = new Map();
    doctorSlots.forEach((s) => { if (s.location_id && !m.has(s.location_id)) m.set(s.location_id, s.location_name || 'Branch'); });
    return Array.from(m, ([id, name]) => ({ id, name }));
  })();
  // Slots for the chosen branch. When multiple branches exist and none is picked
  // yet, show none — the user must choose a branch first (branch → slots, not the
  // reverse). One branch (or online, no branches) → show all.
  const slotsForBranch = selectedBranch
    ? doctorSlots.filter((s) => s.location_id === selectedBranch)
    : (branchesForDate.length > 1 ? [] : doctorSlots);

  // Keep a valid branch selected: preserve if still available, auto-select the
  // only branch, else clear so the user must pick one.
  useEffect(() => {
    const ids = branchesForDate.map((b) => b.id);
    setSelectedBranch((cur) => (cur && ids.includes(cur)) ? cur : (ids.length === 1 ? ids[0] : ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorSlots]);

  const fetchSpecializations = async () => {
    try {
      setLoadingSpecs(true);
      const resp = await axios.get(endpoints.company.specializationsEndUser(companyId));
      setSpecializations(Array.isArray(resp.data) ? resp.data : resp.data?.results || []);
    } catch { /* */ } finally { setLoadingSpecs(false); }
  };
  const fetchDoctors = async (specId) => {
    try {
      setLoadingDoctors(true);
      const resp = await axios.get(endpoints.company.getCompanyDoctor(companyId, specId));
      setDoctors(resp.data?.doctors || []);
      // The doctors-list endpoint returns the tenant's online meeting link
      // alongside the doctor array — stash it so we can show it on the
      // success screen + send the patient to it after booking.
      if (resp.data?.online_meeting_link !== undefined) {
        setOnlineMeetingLink(resp.data.online_meeting_link || '');
      }
    } catch { /* */ } finally { setLoadingDoctors(false); }
  };
  const fetchDoctorSlots = async (docId) => {
    try {
      setLoadingSlots(true);
      const resp = await axios.get(endpoints.company.doctorSlots(docId));
      const d = resp.data?.data || resp.data;
      const walkIns = resp.data?.walk_in_by_date || {};
      setAvailabilityData(d);
      // For walk-in mode, a date is selectable if the doctor accepts walk-ins
      // that day (independent of remaining online slots).
      const slotDates = Object.keys(d).filter((k) => k !== 'range');
      setAvailableDates(
        (offlineOnly
          ? Array.from(new Set([...slotDates, ...Object.keys(walkIns)]))
          : slotDates
        ).sort()
      );
      setLocationsByDate(resp.data?.locations_by_date || {});
    } catch { setAvailabilityData({}); setAvailableDates([]); setLocationsByDate({}); }
    finally { setLoadingSlots(false); }
  };

  // ── Validate ──
  const validate = () => {
    const e = {};
    if (step === 1) {
      if (!patientType) {
        e.patientType = 'Please choose an option';
      } else if (patientType === 'existing') {
        if (!hospitalId.trim()) e.hospitalId = 'Enter your Patient ID';
        else if (lookupLoading) e.hospitalId = 'Checking your ID…';
        else if (!lookupResult?.found) e.hospitalId = 'No patient found with this ID. Check it, or choose "New patient".';
      } else { // 'new'
        if (!name.trim()) e.name = 'Name is required';
      }
    } else if (step === 2) {
      if (!phone || phone.length < 6) e.phone = 'Valid phone number is required';
      if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = 'Enter a valid email';
    } else if (step === 3) {
      if (!selectedDoctor) e.doctor = 'Select a doctor';
      if (!date) e.date = 'Select a date';
      if (!offlineOnly && !time) e.time = 'Select a time';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Switch between "existing patient" / "new patient" (or back to the choice).
  // Reset identity + any looked-up contact so nothing stale carries across.
  const choosePatientType = (type) => {
    setPatientType(type);
    setErrors({});
    setHospitalId(''); setName(''); setLookupResult(null); setNameLockedAlert(false);
    setPhone(''); setEmail('');
  };

  const handleNext = () => {
    if (!validate()) return;
    if (step < TOTAL_STEPS) setStep(step + 1);
    else handleSubmit();
  };
  const handleBack = () => {
    setErrors({});
    // Within step 3, walk back through subsections so users don't lose the screen
    // they were on — only exit to step 2 once we're at the first subsection.
    if (step === 3) {
      if (activeSection === 'time') return setActiveSection(consultationMode !== 'ONLINE' && branchesForDate.length ? 'branch' : 'date');
      if (activeSection === 'branch') return setActiveSection('date');
      if (activeSection === 'date') return setActiveSection('doctor');
      if (activeSection === 'doctor') return setActiveSection('spec');
      // activeSection === 'spec' (or unknown) → fall through to step change below
    }
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const payload = {
        company_id: companyId,
        patient_name: name,
        user_email: email,
        user_mobile: phone,
        hospital_id: hospitalId || undefined,
        doctor_id: selectedDoctor,
      };
      if (offlineOnly) {
        // Walk-in: no slot; backend assigns a queue token.
        payload.date_time = `${date}T00:00:00`;
        payload.booking_type = 'OFFLINE';
      } else {
        payload.date_time = `${date}T${time}:00`;
        payload.consultation_mode = consultationMode;
      }
      const resp = await axios.post(endpoints.appointment.bookAppointment, payload);
      if (offlineOnly) setTokenNumber(resp.data?.token_number ?? null);
      setSubmitted(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      const d = err.response?.data;
      setErrors({ submit: typeof d === 'object' ? Object.values(d).flat().join('. ') : 'Failed to book appointment' });
    } finally { setSubmitting(false); }
  };

  // Auto-scroll to time slots when date is picked
  useEffect(() => {
    if (date && timeRef.current) {
      setTimeout(() => timeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
    }
  }, [date]);

  // Reset activeSection when step changes
  useEffect(() => {
    if (step === 3) setActiveSection(selectedSpec ? (selectedDoctor ? (date ? ((consultationMode !== 'ONLINE' && !selectedBranch && branchesForDate.length > 1) ? 'branch' : 'time') : 'date') : 'doctor') : 'spec');
  }, [step]);

  // Helper: resolve display names
  const specName = specializations.find((s) => s.id === selectedSpec)?.name || '';
  const doctorObj = doctors.find((d) => d.id === selectedDoctor);
  const doctorDisplayName = doctorObj ? formatDoctorName(doctorObj.name) : '';
  const dateObj = date ? new Date(date + 'T00:00:00') : null;
  const dateDisplay = dateObj
    ? `${dateObj.getDate()}${['th','st','nd','rd'][(dateObj.getDate()%100>10&&dateObj.getDate()%100<14)?0:([1,2,3].indexOf(dateObj.getDate()%10)+1)||0]} ${dateObj.toLocaleDateString('en-US',{month:'long'})}`
    : '';

  // Group time slots into two buckets: Morning = 5:00 AM–12:59 PM, Evening = rest.
  const groupSlots = (slots) => {
    const morning = [], evening = [];
    slots.forEach((s) => {
      const [h] = s.value.split(':').map(Number);
      if (h >= 5 && h < 13) morning.push(s);
      else evening.push(s);
    });
    return [
      { label: 'Morning', icon: '🌅', slots: morning },
      { label: 'Evening', icon: '🌇', slots: evening },
    ].filter((g) => g.slots.length > 0);
  };

  // Sentence pill: a clickable chip inline in text.
  // The active section gets a filled background so the user knows where they are;
  // filled-but-inactive shows a subtle ✎ edit hint to convey editability.
  const sentencePill = (label, section, filled = true) => {
    const isActive = activeSection === section;
    const bg = isActive ? ac : (filled ? `${ac}14` : '#F1F5F9');
    const color = isActive ? '#fff' : (filled ? ac : '#94A3B8');
    const borderColor = isActive ? ac : (filled ? `${ac}40` : '#E2E8F0');
    const hoverBg = isActive ? ac : `${ac}22`;
    return (
      <Chip
        size="small" clickable
        label={
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {label}
            {filled && !isActive && (
              <Box component="span" sx={{ fontSize: '0.7rem', opacity: 0.7, lineHeight: 1 }}>✎</Box>
            )}
          </Box>
        }
        onClick={() => setActiveSection(section)}
        sx={{
          height: 28, fontSize: '0.82rem', fontWeight: 700, mx: 0.5, my: 0.25,
          bgcolor: bg, color,
          border: `1.5px solid ${borderColor}`,
          boxShadow: isActive ? `0 2px 8px ${ac}40` : 'none',
          '&:hover': { bgcolor: hoverBg, transform: 'translateY(-1px)' },
          transition: 'all 0.15s',
        }}
      />
    );
  };

  const ac = accentColor; // shorthand

  // ── Pill helper ──
  const pill = (selected, onClick, label, extraSx = {}) => (
    <Chip
      clickable label={label} onClick={onClick}
      sx={{
        fontWeight: 600, fontSize: '0.82rem', height: 36, borderRadius: '18px', px: 0.5,
        bgcolor: selected ? ac : '#F8FAFC', color: selected ? '#fff' : '#334155',
        border: `1.5px solid ${selected ? ac : '#E2E8F0'}`,
        '&:hover': { bgcolor: selected ? ac : `${ac}14` }, transition: 'all 0.15s', ...extraSx
      }}
    />
  );

  const datePill = (d, selected, onClick) => {
    const obj = new Date(d + 'T00:00:00');
    return (
      <Chip key={d} clickable onClick={onClick}
        label={
          <Box sx={{ textAlign: 'center', lineHeight: 1.3 }}>
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: '0.85rem', color: 'inherit' }}>{obj.getDate()}</Typography>
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', textTransform: 'uppercase', opacity: 0.85, color: 'inherit' }}>
              {obj.toLocaleDateString('en-US', { weekday: 'short', month: 'short' })}
            </Typography>
          </Box>
        }
        sx={{
          height: 52, minWidth: 58, borderRadius: 2,
          bgcolor: selected ? ac : '#F8FAFC', color: selected ? '#fff' : '#1E293B',
          border: `1.5px solid ${selected ? ac : '#E2E8F0'}`,
          '&:hover': { bgcolor: selected ? ac : `${ac}14` }, transition: 'all 0.15s',
        }}
      />
    );
  };

  if (!open) return null;

  // Step labels for the in-header stepper
  const stepLabels = ['You', 'Contact', 'Appointment'];

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{
        ...outerSx,
        fontFamily,
        // Make the whole modal follow the tenant's theme fonts (MUI components set
        // their own font otherwise).
        '& .MuiTypography-root, & .MuiButton-root, & .MuiChip-root, & .MuiInputBase-root, & input, & textarea': { fontFamily },
        '& .bk-heading': { fontFamily: headingFontFamily || fontFamily },
      }}>
        {/* ── Sticky Header ── */}
        <Box sx={headerSx}>
          <Box sx={{ px: 3, py: 1.75, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>Book Appointment</Typography>
            </Box>
            <IconButton size="small" onClick={onClose}><CloseOutlined /></IconButton>
          </Box>
          {!submitted && (
            <Stepper steps={stepLabels} current={step} accent={ac} />
          )}
        </Box>

        {/* ── Scrollable body ── */}
        <Box sx={bodySx}>
          {submitted ? (
            (() => {
              // Build a readable "Wed, 28 May at 10:30 AM" string from the picked date+time.
              let when = '';
              try {
                if (offlineOnly && date) {
                  when = new Date(`${date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'long' });
                } else if (date && time) {
                  const d = new Date(`${date}T${time}:00`);
                  when = d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'long' });
                  const t = doctorSlots.find((s) => s.value === time)?.label || time;
                  when = `${when} at ${t}`;
                }
              } catch { /* fall through */ }
              return (
                <Stack spacing={2.5} sx={{ textAlign: 'center', py: 4, alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <CheckCircleOutlined style={{ fontSize: 56, color: ac }} />
                  </Box>
                  <Typography variant="h5" fontWeight={700}>{offlineOnly ? 'Walk-in Registered!' : 'Appointment Booked!'}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 380 }}>
                    Your {offlineOnly ? 'walk-in' : 'appointment'} {when ? <>has been booked for <strong>{when}</strong></> : 'has been booked'}.
                  </Typography>
                  {offlineOnly && tokenNumber != null && (
                    <Box sx={{ bgcolor: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 2, p: 2, maxWidth: 380, width: '100%' }}>
                      <Typography variant="caption" sx={{ display: 'block', color: '#92400E', fontWeight: 700, letterSpacing: '0.05em' }}>
                        YOUR WALK-IN TOKEN
                      </Typography>
                      <Typography sx={{ fontWeight: 800, color: '#B45309', fontSize: '2.5rem', lineHeight: 1.1 }}>
                        #{tokenNumber}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#92400E' }}>
                        Please show this token at the reception.
                      </Typography>
                    </Box>
                  )}
                  {consultationMode === 'ONLINE' && onlineMeetingLink && (
                    <Box sx={{ bgcolor: '#ecfeff', border: '1px solid #67e8f9', borderRadius: 2, p: 1.5, maxWidth: 380, width: '100%' }}>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5, fontWeight: 700 }}>
                        🎥 Online consultation link
                      </Typography>
                      <Box
                        component="a"
                        href={onlineMeetingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ wordBreak: 'break-all', fontSize: '0.85rem', fontWeight: 600, color: '#0e7490', textDecoration: 'underline' }}
                      >
                        {onlineMeetingLink}
                      </Box>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                        Save this link — also sent to your email{email ? '' : ' if provided'}.
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ bgcolor: `${ac}0c`, border: `1px solid ${ac}33`, borderRadius: 2, p: 1.5, maxWidth: 380, width: '100%' }}>
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.25 }}>
                      We'll reach out on
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      📞 +{phone}
                    </Typography>
                    {email && (
                      <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                        ✉️ {email}
                      </Typography>
                    )}
                  </Box>
                  <Button variant="contained" onClick={onClose} sx={{ mt: 1, bgcolor: ac, '&:hover': { bgcolor: ac, opacity: 0.9 }, borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>
                    Done
                  </Button>
                </Stack>
              );
            })()
          ) : (
            <>
              {/* ── STEP 1: Identity ── */}
              {step === 1 && (
                <Stack spacing={2.5} sx={{ py: 1 }}>
                  <Stack spacing={0.5} alignItems="center">
                    <UserOutlined style={{ fontSize: 40, color: ac }} />
                    <Typography variant="h6" fontWeight={700}>Let's identify you</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      {patientType === 'existing'
                        ? "Enter your Patient ID and we'll pull up your details"
                        : patientType === 'new'
                          ? 'Just your name to get started'
                          : 'Have you booked with us before?'}
                    </Typography>
                  </Stack>

                  {/* Choice gate: existing vs new patient */}
                  {!patientType && (
                    <Stack spacing={1.5}>
                      {[
                        { key: 'existing', icon: <IdcardOutlined style={{ fontSize: 24, color: ac }} />, title: "I'm an existing patient", sub: 'Look me up with my Patient ID' },
                        { key: 'new', icon: <UserAddOutlined style={{ fontSize: 24, color: ac }} />, title: "I'm a new patient", sub: 'Booking here for the first time' },
                      ].map((opt) => (
                        <Button
                          key={opt.key}
                          onClick={() => choosePatientType(opt.key)}
                          variant="outlined"
                          fullWidth
                          sx={{
                            justifyContent: 'flex-start', gap: 1.5, px: 2, py: 1.5, textTransform: 'none',
                            borderRadius: 2, borderColor: '#E2E8F0', color: 'inherit',
                            '&:hover': { borderColor: ac, bgcolor: `${ac}0a` },
                          }}
                        >
                          {opt.icon}
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography fontWeight={700} sx={{ lineHeight: 1.2 }}>{opt.title}</Typography>
                            <Typography variant="body2" color="text.secondary">{opt.sub}</Typography>
                          </Box>
                        </Button>
                      ))}
                      {errors.patientType && <Typography variant="caption" color="error">{errors.patientType}</Typography>}
                    </Stack>
                  )}

                  {/* Existing patient → Patient ID (required) + looked-up name */}
                  {patientType === 'existing' && (
                    <>
                      <Button onClick={() => choosePatientType(null)} size="small" startIcon={<ArrowLeftOutlined />}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', color: ac, px: 0.5 }}>
                        Not an existing patient?
                      </Button>
                      <TextField
                        label="Patient ID"
                        placeholder="e.g. ADI0001"
                        value={hospitalId}
                        onChange={(e) => setHospitalId(e.target.value.toUpperCase())}
                        error={!!errors.hospitalId}
                        autoFocus
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><SearchOutlined style={{ fontSize: 16, color: '#94A3B8' }} /></InputAdornment>,
                          endAdornment: lookupLoading ? <CircularProgress size={16} /> : null
                        }}
                        helperText={
                          errors.hospitalId
                            ? errors.hospitalId
                            : lookupResult?.found
                              ? `Welcome back, ${lookupResult.first_name}! ${lookupResult.mobile_masked ? `Phone: ${lookupResult.mobile_masked}` : ''}`
                              : hospitalId.length >= 2 && lookupResult && !lookupResult.found
                                ? 'No patient found with this ID'
                                : 'The ID from your previous visit'
                        }
                        FormHelperTextProps={{ sx: { color: !errors.hospitalId && lookupResult?.found ? '#15803D' : undefined } }}
                        fullWidth
                      />
                      {lookupResult?.found && (
                        <TextField
                          label="Full Name" value={name} fullWidth disabled
                          inputProps={{ style: { textTransform: 'capitalize' } }}
                          helperText="As per our records — contact the clinic to change it."
                        />
                      )}
                    </>
                  )}

                  {/* New patient → just the name */}
                  {patientType === 'new' && (
                    <>
                      <Button onClick={() => choosePatientType(null)} size="small" startIcon={<ArrowLeftOutlined />}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', color: ac, px: 0.5 }}>
                        I have a Patient ID
                      </Button>
                      <TextField
                        label="Full Name"
                        placeholder="Enter your full name"
                        value={name}
                        onChange={(e) => setName(titleCase(e.target.value))}
                        error={!!errors.name}
                        helperText={errors.name}
                        fullWidth required autoFocus
                        inputProps={{ style: { textTransform: 'capitalize' } }}
                      />
                    </>
                  )}
                </Stack>
              )}

              {/* ── STEP 2: Contact ── */}
              {step === 2 && (
                <Stack spacing={2.5} sx={{ py: 1 }}>
                  <Stack spacing={0.5} alignItems="center">
                    <Typography variant="h6" fontWeight={700}>Contact details</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                      We'll use these to confirm your appointment
                    </Typography>
                  </Stack>

                  <Box sx={{
                    '& .react-tel-input .form-control': { width: '100%', height: 48, fontSize: 15, borderRadius: 8 },
                    '& .react-tel-input .flag-dropdown': { borderRadius: '8px 0 0 8px' },
                  }}>
                    <PhoneInput
                      country="in" value={phone} onChange={(v) => setPhone(v)}
                      preferredCountries={['in', 'us', 'gb', 'ae']} enableSearch
                      specialLabel="Phone Number" countryCodeEditable={false}
                    />
                    {errors.phone && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors.phone}</Typography>}
                  </Box>

                  <TextField
                    label="Email Address (Optional)"
                    placeholder="you@example.com"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.toLowerCase().trim())}
                    error={!!errors.email}
                    helperText={errors.email || 'We\'ll send a confirmation if provided'}
                    fullWidth
                  />
                </Stack>
              )}

              {/* ── STEP 3: Doctor / Date / Time — sentence flow ── */}
              {step === 3 && (
                <Stack spacing={2} sx={{ py: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', fontStyle: 'italic' }}>
                    Tap any pill below to change it. Use Back to step through your picks.
                  </Typography>

                  {/* ── Building sentence ── */}
                  <Box sx={{ lineHeight: 2.2, fontSize: '0.98rem', color: '#334155' }}>
                    <Typography component="span" sx={{ fontWeight: 500, fontSize: 'inherit', color: 'inherit' }}>
                      {name.split(' ')[0]}'s appointment for{' '}
                    </Typography>
                    {selectedSpec
                      ? sentencePill(specName, 'spec')
                      : sentencePill('choose specialization', 'spec', false)}
                    {selectedSpec && (
                      <>
                        {selectedDoctor
                          ? sentencePill(doctorDisplayName, 'doctor')
                          : sentencePill('choose doctor', 'doctor', false)}
                      </>
                    )}
                    {selectedDoctor && (
                      <>
                        <Typography component="span" sx={{ fontWeight: 500, fontSize: 'inherit', color: 'inherit' }}> on </Typography>
                        {date
                          ? sentencePill(dateDisplay, 'date')
                          : sentencePill('pick a date', 'date', false)}
                      </>
                    )}
                    {date && consultationMode === 'ONLINE' && (
                      <>
                        <Typography component="span" sx={{ fontWeight: 500, fontSize: 'inherit', color: 'inherit' }}> via </Typography>
                        {sentencePill('🎥 online consultation', 'date')}
                      </>
                    )}
                    {date && consultationMode !== 'ONLINE' && branchesForDate.length > 0 && (
                      <>
                        <Typography component="span" sx={{ fontWeight: 500, fontSize: 'inherit', color: 'inherit' }}> at </Typography>
                        {selectedBranch
                          ? sentencePill(`📍 ${branchesForDate.find((b) => b.id === selectedBranch)?.name || 'branch'}`, 'branch')
                          : sentencePill('choose branch', 'branch', false)}
                      </>
                    )}
                    {date && (
                      <Typography component="span" sx={{ fontWeight: 500, fontSize: 'inherit', color: 'inherit' }}>
                        {' '}should be at{time ? ' ' : '...'}
                      </Typography>
                    )}
                    {time && sentencePill(doctorSlots.find((s) => s.value === time)?.label || time, 'time')}
                  </Box>

                  {/* ── Expandable sections ── */}

                  {/* Specialization */}
                  <Collapse in={activeSection === 'spec'}>
                    <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                      {loadingSpecs ? <CircularProgress size={18} /> : specializations.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">No specializations available</Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {specializations.map((s) => pill(selectedSpec === s.id, () => { setSelectedSpec(s.id); setActiveSection('doctor'); }, s.name))}
                        </Box>
                      )}
                    </Box>
                  </Collapse>

                  {/* Doctor */}
                  {selectedSpec && (
                    <Collapse in={activeSection === 'doctor'}>
                      <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                        {loadingDoctors ? <CircularProgress size={18} /> : doctors.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">No doctors with availability</Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {doctors.map((d) => pill(selectedDoctor === d.id, () => { setSelectedDoctor(d.id); setActiveSection('date'); }, formatDoctorName(d.name)))}
                          </Box>
                        )}
                        {errors.doctor && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors.doctor}</Typography>}
                      </Box>
                    </Collapse>
                  )}

                  {/* Mode picker — shown only when doctor offers both */}
                  {selectedDoctor && (() => {
                    const doc = doctors.find((x) => x.id === selectedDoctor);
                    if (!doc) return null;
                    const inPerson = doc.in_person_enabled !== false;
                    const online = !!doc.online_enabled;
                    if (!inPerson || !online) return null; // only one mode → no picker needed
                    return (
                      <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', mb: 1 }}>
                          How would you like to consult?
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {pill(consultationMode === 'IN_PERSON', () => setConsultationMode('IN_PERSON'), '🏥 In-person')}
                          {pill(consultationMode === 'ONLINE', () => setConsultationMode('ONLINE'), '🎥 Online (video)')}
                        </Box>
                      </Box>
                    );
                  })()}

                  {/* Date */}
                  {selectedDoctor && (
                    <Collapse in={activeSection === 'date'}>
                      <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                        {loadingSlots ? <CircularProgress size={18} /> : availableDates.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">No available dates</Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {availableDates.slice(0, 7).map((d) => datePill(d, date === d, () => { setDate(d); setActiveSection(offlineOnly ? 'date' : (consultationMode === 'ONLINE' ? 'time' : 'branch')); }))}
                          </Box>
                        )}
                        {availableDates.length > 7 && (
                          <TextField
                            type="date" size="small" value={date}
                            onChange={(e) => { setDate(e.target.value); setActiveSection(offlineOnly ? 'date' : (consultationMode === 'ONLINE' ? 'time' : 'branch')); }}
                            inputProps={{ min: availableDates[0], max: availableDates[availableDates.length - 1] }}
                            helperText="Or pick a later date"
                            sx={{ mt: 1, maxWidth: 200 }}
                          />
                        )}
                        {errors.date && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors.date}</Typography>}
                      </Box>
                    </Collapse>
                  )}

                  {/* Branch — pick which branch to visit on the chosen day */}
                  {date && !offlineOnly && consultationMode !== 'ONLINE' && (
                    <Collapse in={activeSection === 'branch'}>
                      <Box sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', display: 'block', mb: 1 }}>
                          Which branch would you like to visit?
                        </Typography>
                        {branchesForDate.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">No branches available on this day</Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {branchesForDate.map((b) => pill(selectedBranch === b.id, () => { setSelectedBranch(b.id); setTime(''); setActiveSection('time'); }, `📍 ${b.name}`))}
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  )}

                  {/* Walk-in: no slot needed — a token is assigned at the desk */}
                  {date && offlineOnly && (
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      No time slot needed for a walk-in — you'll be assigned a <strong>queue token</strong> when you confirm.
                    </Alert>
                  )}

                  {/* Time — filtered to the chosen branch, grouped Morning / Evening */}
                  {date && !offlineOnly && (
                    <Box ref={timeRef}>
                      <Collapse in={activeSection === 'time'}>
                        {/* Selected branch (tap to change) */}
                        {consultationMode !== 'ONLINE' && selectedBranch && (
                          <Box onClick={() => setActiveSection('branch')}
                            sx={{ mb: 1.5, p: 1.25, borderRadius: 2, cursor: 'pointer', bgcolor: `${ac}0f`, border: `1px solid ${ac}44`, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: '1rem' }}>📍</Typography>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="caption" sx={{ color: ac, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', lineHeight: 1.2 }}>
                                Branch{branchesForDate.length > 1 ? ' · tap to change' : ''}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{branchesForDate.find((b) => b.id === selectedBranch)?.name || 'Selected'}</Typography>
                            </Box>
                          </Box>
                        )}
                        {slotsForBranch.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">No slots for this branch on this date</Typography>
                        ) : (
                          <Stack spacing={2}>
                            {groupSlots(slotsForBranch).map((group) => (
                              <Box key={group.label} sx={{ p: 1.5, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                  <Typography sx={{ fontSize: '1rem' }}>{group.icon}</Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                    {group.label}
                                  </Typography>
                                  <Chip size="small" label={`${group.slots.length} slot${group.slots.length === 1 ? '' : 's'}`}
                                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#E2E8F0', color: '#475569' }} />
                                </Stack>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                  {group.slots.map((s) => pill(time === s.value, () => setTime(s.value), s.label, { height: 32, fontSize: '0.78rem' }))}
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        )}
                        {errors.time && <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{errors.time}</Typography>}
                      </Collapse>
                    </Box>
                  )}
                </Stack>
              )}

              {errors.submit && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{errors.submit}</Alert>}
            </>
          )}
        </Box>

        {/* ── Sticky Footer (nav buttons) ── */}
        {!submitted && (
          <Box sx={footerSx}>
            <Button
              variant="outlined" startIcon={<ArrowLeftOutlined />}
              onClick={handleBack} disabled={(step === 1 && activeSection === 'spec') || submitting}
              sx={{ flex: 1, borderColor: ac, color: ac, borderRadius: 2, textTransform: 'none', '&:hover': { borderColor: ac, bgcolor: `${ac}10` } }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              endIcon={step < TOTAL_STEPS ? <ArrowRightOutlined /> : null}
              onClick={handleNext} disabled={submitting}
              sx={{ flex: 1, bgcolor: ac, borderRadius: 2, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: ac, opacity: 0.9 } }}
            >
              {submitting ? <CircularProgress size={20} color="inherit" /> : step === TOTAL_STEPS ? 'Confirm Booking' : 'Continue'}
            </Button>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
